import * as Sentry from "@sentry/react";
import { PARTNER_ERROR_CODES } from "./partnerApi";
import {
  enterSedeMode,
  getActiveSedeId,
  getPartnerSessionId,
  isSedeMode,
  leaveSedeMode,
  setPartnerSessionId,
} from "./partnerStorage";
import { isPartnerAccessEnabled } from "./partnerFlag";
import {
  SedeAccessLostError,
  SedeAccessUnavailableError,
  getEffectiveToken,
  invalidateDelegatedToken,
  peekEffectiveToken,
  resolveEffectiveTokenAtBoot,
} from "./effectiveToken";
import { installCrossTabGuard } from "./crossTabGuard";
import { clearPartnerAccessState, hasPartnerAccessState } from "./partnerDirectory";

// ---------------------------------------------------------------------------
// The one place that ends sede mode from the outside, and the glue the shared
// clients (axios, Apollo, `/verifyToken`) use to stay sede-safe.
//
// Every auth-failure path of the app ends the session by wiping the browser
// storage. In sede mode that would destroy the NORMAL session of every tab and
// of the `apps/next` zone, so those paths ask this module first: a sede
// failure leaves sede mode and reloads into the normal session instead.
//
// Like the rest of `partnerAccess`, nothing here writes to the persistent
// storage of the normal session.
// ---------------------------------------------------------------------------

export const SEDE_LOST_NOTICE_KEY = "partnerAccess.lostReason";

// Reasons for leaving a sede that do not come from the partner API.
export const SEDE_LOST_REASONS = {
  // The backend rejected a delegated token again right after a new mint.
  AUTH_REJECTED: "AUTH_REJECTED",
  // `/verifyToken` could not project the sede identity.
  IDENTITY_FAILED: "IDENTITY_FAILED",
  // The layout was about to end the session (no user data, login error).
  SESSION_INVALID: "SESSION_INVALID",
  // GraphQL answered INVALID_TOKEN to an operation sent as the sede.
  INVALID_TOKEN: "INVALID_TOKEN",
  // The logout request failed after partner access had already ended: the
  // tab reloads into the normal session, still logged in.
  LOGOUT_FAILED: "LOGOUT_FAILED",
};

// Partner API codes (session expired, not a partner…) are expected outcomes.
// These are not: the sede was working and the app had to give it up.
const REPORTED_REASONS = Object.values(SEDE_LOST_REASONS);

// What the callers of the shared clients receive when the token of a request
// could not be resolved.
export const SEDE_ACCESS_LOST_MESSAGE = "SEDE_ACCESS_LOST";
export const SEDE_ACCESS_UNAVAILABLE_MESSAGE = "SEDE_ACCESS_UNAVAILABLE";

// Where the normal session lands after leaving a sede.
const NORMAL_SESSION_ENTRY = "/chat";
const SENTRY_COMPONENT = "partnerAccess";

// Set once the page is on its way out of the sede. It lasts until the reload:
// failures of requests sent as the sede keep arriving meanwhile, and none of
// them may end the normal session.
let sedeAccessLost = false;

/**
 * Leaves `reason` for the normal session to show once after the reload (see
 * `useSedeLostNotice`). For callers that reload on their own; the paths that
 * end sede mode here store it themselves.
 */
export const storeSedeLostNotice = (reason) => {
  try {
    window.sessionStorage.setItem(SEDE_LOST_NOTICE_KEY, String(reason));
  } catch (e) {
    // The notice is a courtesy: leaving the sede does not depend on it.
  }
};

/**
 * Ends sede mode and reloads into the normal session, leaving `reason` as a
 * one-shot notice. Idempotent within a page life. Always returns `true`.
 */
export const handleSedeAccessLost = (reason) => {
  if (sedeAccessLost) return true;
  sedeAccessLost = true;

  invalidateDelegatedToken();
  leaveSedeMode();
  storeSedeLostNotice(reason);
  try {
    Sentry.addBreadcrumb({
      category: SENTRY_COMPONENT,
      message: "sede.accessLost",
      level: "warning",
      data: { reason },
    });
    // An event of its own: a breadcrumb would not survive the reload below.
    if (REPORTED_REASONS.includes(reason)) {
      Sentry.captureMessage("partnerAccess.sedeAccessLost", {
        level: "warning",
        tags: { component: SENTRY_COMPONENT, reason },
      });
    }
  } catch (e) {
    // Telemetry must never keep the tab in a sede it cannot work.
  }
  // Full reload on purpose: the host rebuilds all its state from /verifyToken.
  window.location.replace(NORMAL_SESSION_ENTRY);
  return true;
};

/**
 * For the paths that end the session on an auth failure. `false` outside sede
 * mode: the caller goes on with what it always did. Otherwise the failure
 * belongs to the sede: it ends sede mode instead and returns `true`.
 */
export const handleAuthFailureInSedeMode = (reason) => {
  if (sedeAccessLost) return true;
  if (!isSedeMode()) return false;
  return handleSedeAccessLost(reason);
};

/**
 * What the layout does when the session is no longer valid. In a sede tab the
 * failure belongs to the sede: it is left and nothing else happens (`false`).
 * Otherwise the normal session ends as it always did: login, storage, state.
 * The storage is cleared by the caller's `clearStorage`: nothing in this
 * folder touches the persistent storage itself.
 */
export const endSessionUnlessSede = ({ history, dispatch, clearAction, clearStorage }) => {
  if (handleAuthFailureInSedeMode(SEDE_LOST_REASONS.SESSION_INVALID)) return false;
  history.replace("/login");
  clearStorage();
  dispatch(clearAction());
  return true;
};

/**
 * For clients that do not authenticate with the delegated token (fixed-token
 * APIs, boards): whether an auth failure seen now may end the normal session.
 * Never while the tab is a sede or reloading out of one.
 */
export const canEndNormalSession = () => !sedeAccessLost && !isSedeMode();

/** Returns the reason the sede was left, once. `null` when there is none. */
export const consumeSedeLostNotice = () => {
  try {
    const reason = window.sessionStorage.getItem(SEDE_LOST_NOTICE_KEY);
    if (reason === null || reason === undefined) return null;
    window.sessionStorage.removeItem(SEDE_LOST_NOTICE_KEY);
    return reason;
  } catch (e) {
    return null;
  }
};

// A loss that belongs to a previous owner of the tab (see SedeAccessLostError)
// only fails the request that waited for that mint.
const isStaleLoss = (error) => error instanceof SedeAccessLostError && !error.currentOwner;

/**
 * `getEffectiveToken()` for one request of a shared client. A lost sede ends
 * sede mode here, when it is the sede the tab is on; both failures are
 * rethrown for the client to reject with.
 */
export const getEffectiveTokenForRequest = async () => {
  try {
    return await getEffectiveToken();
  } catch (error) {
    if (error instanceof SedeAccessLostError && !isStaleLoss(error)) {
      handleSedeAccessLost(error.code);
    }
    throw error;
  }
};

/** `{ message, code }` of a token failure, or `null` for any other error. */
export const describeSedeTokenFailure = (error) => {
  if (error instanceof SedeAccessLostError) {
    return { message: SEDE_ACCESS_LOST_MESSAGE, code: error.code };
  }
  if (error instanceof SedeAccessUnavailableError) {
    return { message: SEDE_ACCESS_UNAVAILABLE_MESSAGE, code: error.code };
  }
  return null;
};

/**
 * For flows that cannot go on without an identity (`/verifyToken`). The tab
 * cannot be a sede without a delegated token, so a failure of the sede it is
 * on leaves sede mode and resolves `null`: `null` only ever means "sede mode
 * was left, a reload is under way". Outside sede mode it resolves the normal
 * JWT. The one failure that leaves nothing (the owner of the tab changed
 * again while the retry waited) rejects with `SedeAccessUnavailableError`.
 */
export const getEffectiveTokenOrLeaveSede = async () => {
  try {
    return await getEffectiveToken().catch((error) => {
      if (!isStaleLoss(error)) throw error;
      // The identity is the one of whoever is on the tab now: ask for theirs.
      return getEffectiveToken();
    });
  } catch (error) {
    // Stale twice: no sede was left and nothing reloads. Transient, like an
    // outage: sede mode is kept and a later call may succeed.
    if (isStaleLoss(error)) throw new SedeAccessUnavailableError(PARTNER_ERROR_CODES.UNAVAILABLE);
    const failure = describeSedeTokenFailure(error);
    handleSedeAccessLost(failure ? failure.code : PARTNER_ERROR_CODES.UNAVAILABLE);
    return null;
  }
};

/**
 * Drops the cached delegated token when it is the one the backend rejected.
 * Many requests can fail at once with the same token: only the first of them
 * may drop it, or each one would cost a mint.
 */
export const discardRejectedDelegatedToken = (authorizationHeader) => {
  if (authorizationHeader === `Bearer ${peekEffectiveToken()}`) invalidateDelegatedToken();
};

const recordTransition = (message, companyId) => {
  try {
    Sentry.addBreadcrumb({
      category: SENTRY_COMPONENT,
      message,
      level: "info",
      data: { companyId },
    });
  } catch (e) {
    // Telemetry must never decide whether a sede can be opened.
  }
};

/**
 * Makes `companyId` the sede of this tab, with the partner session the tab
 * already has, and reloads into its chat. `false` (and no reload) when the
 * sede could not be stored.
 */
export const switchToSede = (companyId) => {
  if (!enterSedeMode({ companyId, sessionId: getPartnerSessionId() })) return false;
  // Full reload on purpose: the host rebuilds all its state from /verifyToken.
  window.location.assign(NORMAL_SESSION_ENTRY);
  return true;
};

/**
 * Back to the partner's own company. The partner session id stays in the tab:
 * the sede list keeps working with it and logout still has it to revoke.
 */
export const returnToOwnCompany = () => {
  const sessionId = getPartnerSessionId();
  leaveSedeMode();
  setPartnerSessionId(sessionId);
  invalidateDelegatedToken();
  window.location.assign(NORMAL_SESSION_ENTRY);
};

const SEDE_URL_PARAM = "sede";
const COMPANY_ID_PATTERN = /^[0-9a-f]{24}$/;

/**
 * Opens the chat of a sede in a new tab. The tab state is not shared between
 * tabs, so the new one learns its sede from the URL (see the handoff below).
 */
export const openSedeInNewTab = (companyId) => {
  if (typeof companyId !== "string" || !COMPANY_ID_PATTERN.test(companyId)) return;
  recordTransition("sede.openInNewTab", companyId);
  window.open(`${NORMAL_SESSION_ENTRY}?${SEDE_URL_PARAM}=${companyId}`, "_blank", "noopener");
};

const paramName = (pair) => {
  try {
    return decodeURIComponent(pair.split("=")[0]);
  } catch (e) {
    return null;
  }
};

const paramValue = (pair) => {
  try {
    return decodeURIComponent(pair.split("=").slice(1).join("="));
  } catch (e) {
    return "";
  }
};

// `?sede=<companyId>` is how a tab opened by `openSedeInNewTab` starts as a
// sede. The parameter is consumed: it leaves the address bar whatever its
// value, and every other parameter stays byte for byte as it was.
// Any link can carry the parameter, so it only acts on a tab that works no
// sede yet: a tab already on a sede keeps the one its user chose and just
// drops the parameter (the backend still refuses sedes outside the group).
const applySedeHandoffFromUrl = () => {
  if (!isPartnerAccessEnabled()) return;
  try {
    const { pathname, search, hash } = window.location;
    const pairs = String(search || "").replace(/^\?/, "").split("&").filter(Boolean);
    const handoff = pairs.filter((pair) => paramName(pair) === SEDE_URL_PARAM);
    if (handoff.length === 0) return;

    const activeSedeId = getActiveSedeId();
    if (activeSedeId !== null) {
      recordTransition("sede.handoffIgnored", activeSedeId);
    } else {
      // An id that is not valid is refused by `enterSedeMode` itself.
      enterSedeMode({ companyId: paramValue(handoff[0]), sessionId: getPartnerSessionId() });
    }

    const kept = pairs.filter((pair) => !handoff.includes(pair));
    const query = kept.length > 0 ? `?${kept.join("&")}` : "";
    window.history.replaceState(window.history.state, "", `${pathname}${query}${hash || ""}`);
  } catch (e) {
    // The handoff is a convenience: boot goes on without it.
  }
};

/**
 * To be awaited before the first render: the boards Pusher is built on it and
 * needs the effective token resolved. Outside sede mode it resolves at once.
 * Never rejects, so the app always renders.
 */
export const resolveSedeBoot = async () => {
  try {
    installCrossTabGuard({
      // Requests sent as the sede keep failing until the reload, when the tab
      // is no longer a sede: none of them may end the session another tab
      // has just started.
      onSedeTabDropped: () => {
        sedeAccessLost = true;
      },
    });
    applySedeHandoffFromUrl();
    // With the flag off a tab that still holds a sede (the flag was turned off
    // while it was open) boots as the normal session: nothing would let the
    // user leave the sede otherwise. No notice, no request.
    if (!isPartnerAccessEnabled() && hasPartnerAccessState()) clearPartnerAccessState();
    const boot = await resolveEffectiveTokenAtBoot();
    if (boot.lostReason) storeSedeLostNotice(boot.lostReason);
    return boot;
  } catch (e) {
    return { mode: "normal" };
  }
};
